import React from 'react';
import useTranslate from '../../../Hooks/useTranslate';
import { Box, Typography, Divider, Chip } from '@mui/material';
import { themeAtom } from '../../../Atoms';
import DoneAllRoundedIcon from '@mui/icons-material/DoneAllRounded';
import DoneRoundedIcon from '@mui/icons-material/DoneRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import {
  addZero,
  createNewDate,
  options,
  culture,
  compareDates,
} from '../../../Helpers/dates';
import { useAtomValue } from 'jotai';
import MessageFeedback from '../../../Components/Button/MessageFeedback';

const useStyles = () => ({
  root: {
    display: 'flex',
    width: '100%',
    alignItems: 'center',
  },
  msgBox: {
    width: '80%',
    padding: '8px 8px 0px 8px',
    position: 'relative',
    borderRadius: '10px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
});

const SMS_STATUSES = {
  ERROR: (
    <ErrorRoundedIcon
      sx={{
        fontSize: '1.1rem',
        color: '#596373',
      }}
    />
  ),
  DLVRD: <DoneAllRoundedIcon sx={{ color: '#596373', fontSize: '1.1rem' }} />,
  SENT: <DoneRoundedIcon sx={{ color: '#596373', fontSize: '1.1rem' }} />,
};

const MessageBox = function MessageBox({
  msgId,
  value,
  outbound,
  campaignId,
  handleReadMessage,
  readOn = new Date(),
  performedOn = null,
  readBy = '',
  status = null,
  previousItem = {},
  outcome,
  campaignAdvancement,
}) {
  const translate = useTranslate();
  const theme = useAtomValue(themeAtom);
  const classes = useStyles();
  const showDate = performedOn
    ? !compareDates(new Date(previousItem?.performedOn), new Date(performedOn))
    : false;
  const handleUpdate = () => {
    handleReadMessage(msgId);
  };
  return (
    <div style={{ width: '100%' }}>
      {showDate && (
        <Divider sx={{ p: '16px 0px' }}>
          <Chip
            label={createNewDate(performedOn).toLocaleDateString(
              culture,
              options
            )}
          />
        </Divider>
      )}
      <div
        style={{
          ...classes.root,
          justifyContent: outbound ? 'flex-end' : 'flex-start',
        }}
      >
        <Box
          sx={{
            ...classes.msgBox,
            backgroundColor: outbound
              ? theme === 'dark'
                ? 'primary.main'
                : '#C1D8FB'
              : theme === 'dark'
                ? '#2C2C2C'
                : '#EBEBEB',
          }}
        >
          {value ? (
            <Typography variant="body2" sx={{ textAlign: 'left' }}>
              {value}
            </Typography>
          ) : (
            <Typography
              variant="body2"
              sx={{ fontStyle: 'italic', color: 'text.secondary' }}
            >
              {translate('MESSAGE_WITH_NO_CONTENT')}
            </Typography>
          )}
          <Box
            sx={{
              display: 'flex',
              justifyContent: readBy ? 'space-between' : 'flex-end',
              alignItems: 'center',
              gap: '8px',
              p: '2px',
            }}
          >
            {readBy && (
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.8rem',
                }}
              >
                {`${translate('READ_BY')} ${readBy} ${translate('READ_ON')} ${
                  readOn
                    ? new Date(readOn)?.toLocaleDateString(culture, {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                      })
                    : ''
                }`}
              </Typography>
            )}
            <Typography variant="caption" sx={{ fontSize: '0.8rem' }}>
              {performedOn ? addZero(createNewDate(performedOn)) : ''}
            </Typography>
            {status ? SMS_STATUSES[status] : null}
          </Box>
        </Box>
        {!outbound && (
          <Box sx={{ marginLeft: 'auto' }}>
            <MessageFeedback
              id={msgId}
              onUpdate={handleUpdate}
              campaignId={campaignId}
              detailsData={
                readBy
                  ? {
                      completedByFullName: readBy,
                      completedOn: readOn,
                      outcome,
                      campaignAdvancement: campaignAdvancement ?? 0,
                    }
                  : null
              }
            />
          </Box>
        )}
      </div>
    </div>
  );
};

export default MessageBox;
